import type SearchOrigin from '@alltrails/amplitude/enums/SearchOrigin';
import SearchRouteTypeFilter from '@alltrails/amplitude/enums/SearchRouteTypeFilter';
import type SearchSortFilter from '@alltrails/amplitude/enums/SearchSortFilter';
import SearchTrailTrafficFilter from '@alltrails/amplitude/enums/SearchTrailTrafficFilter';
import type BoundingBox from '../types/BoundingBox';
import type DistanceAway from '../types/DistanceAway';
import type ExploreServiceFilters from '../types/ExploreServiceFilters';
import type { ExploreServiceTrailDifficulty } from '../types/ExploreServiceFilters';
import type { ExploreServiceSearchResultsSummary } from '../types/ExploreServiceResponse';
import type ExploreServiceSort from '../types/ExploreServiceSort';
import type TrailEngagement from '../types/TrailEngagement';
import { defaultExploreServiceSort } from './constants';

export type BuildExploreFiltersAppliedParamsState = {
  boundingBox: BoundingBox | undefined;
  distanceAway: DistanceAway | undefined;
  filters: ExploreServiceFilters;
  searchResultsSummary: ExploreServiceSearchResultsSummary | undefined;
  sort: ExploreServiceSort;
  trailEngagement: TrailEngagement[];
  useMetric: boolean;
  searchOrigin?: SearchOrigin;
};

export type BuildExploreFiltersAppliedParamsOverrides = {
  distanceAway?: DistanceAway | undefined;
  filters?: ExploreServiceFilters;
  sort?: ExploreServiceSort;
  trailEngagement?: TrailEngagement[];
};

export function isDefaultFilterState(state: {
  distanceAway: DistanceAway | undefined;
  filters: ExploreServiceFilters;
  sort: ExploreServiceSort;
  trailEngagement: TrailEngagement[];
}): boolean {
  const filterCount = Object.keys(state.filters ?? {}).length;
  return (
    filterCount === 0 &&
    (state.sort ?? defaultExploreServiceSort) === defaultExploreServiceSort &&
    state.distanceAway === undefined &&
    (state.trailEngagement?.length ?? 0) === 0
  );
}

function countFilterGroupsAndItems(
  filters: ExploreServiceFilters,
  distanceAway: DistanceAway | undefined,
  trailEngagement: TrailEngagement[]
): { numFiltersSelected: number; numFilterItemsSelected: number } {
  let numFiltersSelected = 0;
  let numFilterItemsSelected = 0;

  if (distanceAway?.distance) {
    numFiltersSelected += 1;
    numFilterItemsSelected += 1;
  }

  trailEngagement.forEach(() => {
    numFilterItemsSelected += 1;
  });
  if (trailEngagement.length > 0) {
    numFiltersSelected += 1;
  }

  const addArray = (arr: unknown[] | undefined) => {
    if (arr?.length) {
      numFiltersSelected += 1;
      numFilterItemsSelected += arr.length;
    }
  };
  const addRange = (range: { min?: number; max?: number } | undefined) => {
    if (range && (range.min !== undefined || range.max !== undefined)) {
      numFiltersSelected += 1;
      numFilterItemsSelected += 1;
    }
  };
  const addSingle = (value: unknown) => {
    if (value !== undefined && value !== null) {
      numFiltersSelected += 1;
      numFilterItemsSelected += 1;
    }
  };

  addArray(filters.activity);
  addArray(filters.difficulty);
  addRange(filters.length);
  addRange(filters.elevationGain);
  addArray(filters.feature);
  addArray(filters.routeType);
  addArray(filters.suitability);
  addArray(filters.trailTraffic);
  addRange(filters.highestPoint);
  addSingle(filters.minRating);

  return { numFiltersSelected, numFilterItemsSelected };
}

function exploreSortToSearchSortFilter(sort: ExploreServiceSort): SearchSortFilter {
  if (sort === 'best_match') return 'bestMatch';
  if (sort === 'most_popular') return 'mostPopular';
  if (sort === 'newly_added') return 'newlyAdded';
  if (sort === 'closest' || sort === 'seasonal') return sort;
  if (sort?.startsWith?.('month_')) return 'seasonal';
  return 'bestMatch';
}

function formatLocationFilters(boundingBox: BoundingBox | undefined): string | undefined {
  if (!boundingBox) return undefined;
  const northeast = boundingBox.topRight;
  return JSON.stringify({ lat: northeast.lat, lng: northeast.lng });
}

function formatRange(range: { min?: number; max?: number } | undefined): string | undefined {
  if (!range || (range.min === undefined && range.max === undefined)) return undefined;
  return JSON.stringify(range);
}

/**
 * Builds params for the "explore filters applied" Amplitude event.
 * Used when user applies filters via the All filters modal ("Show trails" button) or via a filter chip.
 */
export default function buildExploreFiltersAppliedParams(
  state: BuildExploreFiltersAppliedParamsState,
  overrides?: BuildExploreFiltersAppliedParamsOverrides
): {
  activity_type_filters?: string;
  attractions_filters?: string[];
  difficulty_filters?: ExploreServiceTrailDifficulty[];
  distance_away_filter?: number;
  elevation_filters?: string;
  highest_point_filter?: string;
  is_metric_user?: boolean;
  length_filters?: string;
  location_filters?: string;
  num_filter_items_selected: number;
  num_filters_selected: number;
  num_results_returned: number;
  rating_filters?: number;
  route_type_filters?: SearchRouteTypeFilter[];
  search_origin: SearchOrigin;
  sort_filters?: SearchSortFilter;
  suitability_filters?: string[];
  trail_completion_filters?: ('completed' | 'not_completed' | 'verified_completed')[];
  trail_traffic_filters?: SearchTrailTrafficFilter[];
} {
  const filters = overrides?.filters ?? state.filters;
  const sort = overrides?.sort ?? state.sort;
  const trailEngagement = overrides?.trailEngagement ?? state.trailEngagement;
  const distanceAway = overrides?.distanceAway !== undefined ? overrides.distanceAway : state.distanceAway;

  const { numFiltersSelected, numFilterItemsSelected } = countFilterGroupsAndItems(filters, distanceAway, trailEngagement);

  const trailCompletionFromEngagement = trailEngagement.filter(
    (e): e is 'completed' | 'not_completed' => e === 'completed' || e === 'not_completed'
  ) as ('completed' | 'not_completed' | 'verified_completed')[];

  return {
    activity_type_filters: filters.activity?.length ? JSON.stringify(filters.activity) : undefined,
    attractions_filters: filters.feature?.length ? filters.feature : undefined,
    difficulty_filters: filters.difficulty,
    distance_away_filter: distanceAway?.distance,
    elevation_filters: formatRange(filters.elevationGain),
    highest_point_filter: formatRange(filters.highestPoint),
    is_metric_user: state.useMetric,
    length_filters: formatRange(filters.length),
    location_filters: formatLocationFilters(state.boundingBox),
    num_filter_items_selected: numFilterItemsSelected,
    num_filters_selected: numFiltersSelected,
    num_results_returned: state.searchResultsSummary?.count ?? 0,
    rating_filters: filters.minRating,
    route_type_filters: filters.routeType,
    search_origin: state.searchOrigin ?? 'explore map view',
    sort_filters: exploreSortToSearchSortFilter(sort),
    suitability_filters: filters.suitability,
    trail_completion_filters: trailCompletionFromEngagement.length > 0 ? trailCompletionFromEngagement : undefined,
    trail_traffic_filters: filters.trailTraffic
  };
}
